import { Db } from 'mongodb'
import { schema, types } from 'papr'
import { VISIBILITY } from '../shared'
import { PROJECT_DELETION_TTL } from '../shared/constants'
import { SIDEBAR_ITEM_STATUS } from './docs-page'

export const CUSTOM_DOMAIN_STATUS = ['VERIFIED' as const, 'FAILED' as const, 'PENDING' as const]
export type CustomDomainStatus = (typeof CUSTOM_DOMAIN_STATUS)[number]

const collectionName = 'docsproject'

export const PROJECT_MEMBER_ROLES = ['OWNER' as const, 'ADMIN' as const]

export const GUIDE_STATUS = ['UNPUBLISHED', 'PUBLISHED'] as const

export const VERSION_STATUS = [
	'STABLE' as const,
	'UNSTABLE' as const,
	'DEPRECATED' as const,
] as const

export type VersionStatus = (typeof VERSION_STATUS)[number]

export const DOCS_GUIDE_PROVIDERS = ['GITHUB' as const, 'HASHNODE' as const]

export type DocsGuideProvider = (typeof DOCS_GUIDE_PROVIDERS)[number]

const navBarItemBaseSchema = {
	id: types.objectId({ required: true }),
	label: types.string({ required: true }),
	createdAt: types.date({ required: true }),
	updatedAt: types.date(),
}

const navBarItemLinkSchema = types.object(
	{
		...navBarItemBaseSchema,
		type: 'LINK' as const,
		url: types.string({ required: true }),
		newTab: types.boolean({ required: true }),
	},
	{
		required: true,
	},
)

const navBarItemGuideSchema = types.object(
	{
		...navBarItemBaseSchema,
		type: 'GUIDE' as const,
		guideId: types.objectId({ required: true }),
	},
	{
		required: true,
	},
)

const navBarItemPageSchema = types.object(
	{
		...navBarItemBaseSchema,
		type: 'PAGE' as const,
		pageId: types.objectId({ required: true }),
		newTab: types.boolean({ required: true }),
	},
	{
		required: true,
	},
)

const navBarColumnSchema = types.object(
	{
		id: types.objectId({ required: true }),
		label: types.string({ required: true }),
		items: types.array(
			types.oneOf([navBarItemLinkSchema, navBarItemGuideSchema, navBarItemPageSchema]),
			{
				required: true,
			},
		),
		createdAt: types.date({ required: true }),
		updatedAt: types.date(),
	},
	{
		required: true,
	},
)

const memberSchema = types.object(
	{
		userId: types.objectId({ required: true }),
		role: types.enum(PROJECT_MEMBER_ROLES, { required: true }),
		createdAt: types.date({ required: true }),
		updatedAt: types.date(),
	},
	{
		required: true,
	},
)

const sidebarItemBaseSchema = {
	id: types.objectId({ required: true }),
	label: types.string({ required: true }),
	status: types.enum(SIDEBAR_ITEM_STATUS, { required: true }),
	visibility: types.enum(VISIBILITY, {
		required: true,
	}),
	createdAt: types.date({ required: true }),
	updatedAt: types.date(),
}

const pageBaseSchema = {
	...sidebarItemBaseSchema,
	variant: 'PAGE' as const,
	path: types.string({ required: true }),
	/**
	 * True for:
	 * - New page that is unpublished
	 * - Draft title or content is different to published title or content
	 */
	hasChanges: types.boolean({ required: true }),
}

const pageLevelThreeSchema = types.object(
	{
		...pageBaseSchema,
		// For now, we only allow pages as children of pages up to three levels
		// to account for further expansion, we initialize the last level with unknown pages
		pages: types.array(types.any(), { required: true }),
	},
	{
		required: true,
	},
)

// Page Level 1 -> Page Level 2: Only pages as children no sections
const pageLevelTwoSchema = types.object(
	{
		...pageBaseSchema,

		pages: types.array(pageLevelThreeSchema, { required: true }),
	},
	{
		required: true,
	},
)

const pageSchema = types.object(
	{
		...pageBaseSchema,
		pages: types.array(pageLevelTwoSchema, { required: true }),
	},
	{
		required: true,
	},
)

const sectionSchema = types.object(
	{
		...sidebarItemBaseSchema,
		variant: 'SECTION' as const,
		path: types.string({ required: true }),
		pages: types.array(pageSchema, { required: true }),
	},
	{
		required: true,
	},
)

const linkSchema = types.object(
	{
		...sidebarItemBaseSchema,
		variant: 'LINK' as const,
		url: types.string({ required: true }),
	},
	{
		required: true,
	},
)

const versionBaseSchema = {
	id: types.objectId({ required: true }),
	slug: types.string({ required: true }),
	/**
	 * The name of the version.
	 *
	 * @example "v1.0.0"
	 * @example "v1"
	 * @example "2024-05"
	 */
	name: types.string({ required: true }),
	codeName: types.string(),
	/**
	 * There is always exactly one default version per guide.
	 *
	 * Default versions should not be deleted or hidden.
	 */
	isDefault: types.boolean({ required: true }),
	visibility: types.enum(VISIBILITY, {
		required: true,
	}),
	/**
	 * The status of the version indicating its stability.
	 *
	 * "STABLE" is considered the default status.
	 */
	status: types.enum(VERSION_STATUS, { required: true }),
	/**
	 * The id of the version that this version was forked from.
	 */
	forkedFrom: types.objectId(),
	createdAt: types.date({ required: true }),
	updatedAt: types.date(),
	/**
	 * The time when the version was soft-deleted.
	 */
	deletedAt: types.date(),
}
export type VersionBaseSchema = typeof versionBaseSchema

const apiReferenceGuideVersionSchema = types.object(
	{
		...versionBaseSchema,
		variant: 'API_REFERENCE' as const,
		/**
		 * The URL pointing to the OpenAPI definition for this API reference.
		 */
		url: types.string({ required: true }),
	},
	{
		required: true,
	},
)
export type ApiReferenceGuideVersionSchema = typeof apiReferenceGuideVersionSchema

const documentationGuideVersionSchema = types.object(
	{
		...versionBaseSchema,
		variant: 'GUIDE' as const,
		sidebarItems: types.array(types.oneOf([pageSchema, sectionSchema, linkSchema]), {
			required: true,
		}),
	},
	{
		required: true,
	},
)
export type DocumentationGuideVersionSchema = typeof documentationGuideVersionSchema

const guideBaseSchema = {
	id: types.objectId({ required: true }),
	slug: types.string({ required: true }),
	name: types.string({ required: true }),
	ogMetaData: types.object(
		{
			image: types.string(),
		},
		{ required: true },
	),
	seo: types.object(
		{
			title: types.string(),
			description: types.string(),
			shouldNotIndex: types.boolean(),
		},
		{ required: true },
	),
	status: types.enum(GUIDE_STATUS, {
		required: true,
	}),
	isLocked: types.boolean(),
	/**
	 * Indicates if the provided is not our own Editor but a external source like GitHub
	 */
	provider: types.object({
		type: types.enum(DOCS_GUIDE_PROVIDERS, { required: true }),
	}),
	createdAt: types.date({ required: true }),
	updatedAt: types.date(),
}

const apiReferenceGuideSchema = types.object(
	{
		...guideBaseSchema,
		variant: 'API_REFERENCE' as const,
		versions: types.array(apiReferenceGuideVersionSchema, {
			required: true,
			minItems: 1,
		}),
	},
	{
		required: true,
	},
)
export type DocsAPIReferenceGuideDocument = typeof apiReferenceGuideSchema

const documentationGuideSchema = types.object(
	{
		...guideBaseSchema,
		variant: 'GUIDE' as const,
		versions: types.array(documentationGuideVersionSchema, {
			required: true,
			minItems: 1,
		}),
	},
	{
		required: true,
	},
)
export type DocsDocumentationGuideDocument = typeof documentationGuideSchema

const docsProjectCustomDomainSchema = types.object(
	{
		status: types.enum(CUSTOM_DOMAIN_STATUS, { required: true }),
		domain: types.string({ required: true }),
		verifiedAt: types.date(),
		wwwDomain: types.object({
			status: types.enum(CUSTOM_DOMAIN_STATUS, { required: true }),
			verifiedAt: types.date(),
		}),
		executionArn: types.string(),
		createdAt: types.date({ required: true }),
		updatedAt: types.date(),
	},
	{ required: false },
)

const aiPromptSchema = types.object(
	{
		id: types.objectId({ required: true }),
		prompt: types.string({ required: true }),
		createdAt: types.date({ required: true }),
	},
	{
		required: true,
	},
)

const githubSync = types.object(
	{
		installationId: types.string({ required: true }),
		accessToken: types.string({ required: true }),
		owner: types.string({ required: true }),
		repository: types.string({ required: true }),
		createdAt: types.date({ required: true }),
		rootFolder: types.string(),
	},
	{
		timestamps: true,
		required: false,
	},
)

export const SUBSCRIPTION_STATUS = [
	'ACTIVE' as const,
	'PAST_DUE' as const,
	'UNPAID' as const,
	'CANCELED' as const,
]
export const PRODUCT_NAME = ['STARTUP' as const, 'ENTERPRISE' as const]

const subscriptionSchema = types.object({
	stripeCustomerId: types.string({ required: true }),
	stripeSubscriptionId: types.string({ required: true }),
	status: types.enum(SUBSCRIPTION_STATUS, { required: true }),
	// it is a "product" in Stripe, we would maybe call it "plan"
	productName: types.enum(PRODUCT_NAME, { required: true }),
	nextBillingCycle: types.date(),
	maxSeats: types.number({ required: true }),
})

export const docsProjectSchema = schema(
	{
		name: types.string({ required: true }),
		subscription: subscriptionSchema,
		subdomain: types.string({ required: true }),
		status: types.enum(['ACTIVE' as const, 'DEACTIVATED' as const], {
			required: true,
		}),
		customDomain: docsProjectCustomDomainSchema,
		description: types.string(),
		appearance: types.object(
			{
				logo: types.string(),
				logoDarkMode: types.string(),
				favicon: types.string(),
				primaryColor: types.string(),
				defaultDocsTheme: types.enum(['LIGHT' as const, 'DARK' as const], {
					required: true,
				}),
				getStarted: types.object({
					label: types.string({ required: true }),
					url: types.string({ required: true }),
				}),
				customScript: types.string(),
			},
			{ required: true },
		),
		settings: types.object(
			{
				allowRobots: types.boolean(),
				allowHashnodeLogin: types.boolean(),
				isHeadless: types.boolean(),
			},
			{ required: true },
		),
		links: types.object(
			{
				twitter: types.string(),
				instagram: types.string(),
				github: types.string(),
				website: types.string(),
				hashnode: types.string(),
				youtube: types.string(),
				dailydev: types.string(),
				linkedin: types.string(),
				mastodon: types.string(),
				githubRepository: types.string(),
				bluesky: types.string(),
			},
			{ required: true },
		),
		integrations: types.object({
			gaTrackingID: types.string(),
			gTagManagerID: types.string(),
			hotjarSiteID: types.string(),
			fbPixelID: types.string(),
			intercomID: types.string(),
			metaTags: types.string(),
			msClarityID: types.string(),
			koalaPublicKey: types.string(),
		}),
		// Guides need to be required and need to be at least an empty array so that we can push guides to it
		guides: types.array(types.oneOf([documentationGuideSchema, apiReferenceGuideSchema]), {
			required: true,
		}),
		members: types.array(memberSchema, { required: true }),
		features: types.object({
			collaboration: types.boolean(),
			ghSync: types.boolean(),
			versioning: types.boolean(),
		}),
		navigation: types.object({
			header: types.array(
				types.oneOf([navBarItemLinkSchema, navBarItemGuideSchema, navBarItemPageSchema]),
				{
					required: true,
				},
			),
			footer: types.array(navBarColumnSchema, { required: true }),
		}),
		ai: types.object({
			prompts: types.array(aiPromptSchema, { required: true }),
			settings: types.object({
				isSearchEnabled: types.boolean({ required: true }),
			}),
		}),
		preview: types.object({
			creationDate: types.date({ required: true }),
			linkedProjectId: types.objectId({ required: true }),
		}),
		githubSync: githubSync,
		/**
		 * The time when the project was soft deleted.
		 * User can request to restore the project for a small duration. After that, the project will be permanently deleted.
		 */
		deletedAt: types.date(),
	},
	{
		// This one sets updatedAt as mandatory -> Let's make all updatedAt mandatory
		timestamps: true,
	},
)

export const createDocsProjectIndexes = async (db: Db) => {
	await Promise.all([
		db.createIndex(collectionName, {
			subdomain: 1,
			status: 1,
		}),
		db.createIndex(collectionName, {
			'customDomain.domain': 1,
			status: 1,
		}),
		db.createIndex(collectionName, {
			'customDomain.wwwDomain': 1,
			status: 1,
		}),
		db.createIndex(collectionName, {
			'members.userId': 1,
			role: 1,
			status: 1,
		}),
		db.createIndex(
			collectionName,
			{
				deletedAt: 1,
			},
			{
				expireAfterSeconds: PROJECT_DELETION_TTL,
			},
		),
	])
}

export type DocsProjectDocument = (typeof docsProjectSchema)[0]
export type DocsProjectSchemaOptions = (typeof docsProjectSchema)[1]

export type DocsGuideDocument = DocsAPIReferenceGuideDocument | DocsDocumentationGuideDocument

export type DocsVersion = ApiReferenceGuideVersionSchema | DocumentationGuideVersionSchema

export type DocsSectionDocument = typeof sectionSchema
export type DocsLinkDocument = typeof linkSchema
export type DocsRootPageDocument = typeof pageSchema

export type DocsSidebarItemRootDocument =
	| DocsRootPageDocument
	| DocsSectionDocument
	| DocsLinkDocument

export type DocsSidebarItemNestableRoot = DocsRootPageDocument | DocsSectionDocument

export type DocsSidebarItemDocument =
	| DocsSidebarItemRootDocument
	| typeof pageLevelTwoSchema
	| typeof pageLevelThreeSchema

export type DocsPageMetadataDocument =
	| typeof pageSchema
	| typeof pageLevelTwoSchema
	| typeof pageLevelThreeSchema

export type CustomDomainSchema = typeof docsProjectCustomDomainSchema

export type DocumentationNavbarItemLinkSchema = typeof navBarItemLinkSchema
export type DocumentationNavbarItemGuideSchema = typeof navBarItemGuideSchema
export type DocumentationNavbarItemPageSchema = typeof navBarItemPageSchema

export type DocumentationNavbarItemSchema =
	| DocumentationNavbarItemLinkSchema
	| DocumentationNavbarItemGuideSchema
	| DocumentationNavbarItemPageSchema
export type DocumentationNavbarItemSchemaType = DocumentationNavbarItemSchema['type']

export type DocumentationNavbarColumnSchema = typeof navBarColumnSchema

export type DocumentationNavigation = NonNullable<DocsProjectDocument['navigation']>

export type AIPromptSchema = typeof aiPromptSchema
